import { HttpClient } from '@angular/common/http';
import { Component, ElementRef, HostListener, OnInit, SecurityContext, ViewChild } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { ColumnModel, FilterSettingsModel, GridComponent, RowDeselectEventArgs, RowSelectEventArgs, SelectionSettingsModel } from '@syncfusion/ej2-angular-grids';
import { PdfJsViewerComponent } from 'ng2-pdfjs-viewer';
import { ClipboardService } from 'ngx-clipboard';
import { fromEvent, Observable, Subject } from 'rxjs';
import { setSpinner } from '@syncfusion/ej2-angular-popups';
import { ApplicationState, State, getApplicationState, ToastUserNotification, PostingPlanDetail, OpenEngDrawing } from 'src/app/state-management';
import { takeUntil } from 'rxjs/operators';
import * as jszip from 'jszip';
const JSZip = require('jszip');
import { saveAs } from 'file-saver';

@Component({
    selector: 'app-postingplans',
    templateUrl: './postingplans.component.html',
    styleUrls: ['./postingplans.component.scss'],
    providers: [PdfJsViewerComponent],
    standalone: false
})
export class PostingPlansComponent implements OnInit {
  application$: Observable<ApplicationState>;
  applicationState: ApplicationState;

  private ngUnsubscribe: Subject<void> = new Subject();
  public drawings: any = [];
  public columns: ColumnModel[] = [];
  public filterOption: FilterSettingsModel = { type: 'Excel' };
  public selectionOptions: SelectionSettingsModel = { mode: 'Row', type: 'Multiple', enableSimpleMultiRowSelection: true };

  public selectedPlanUrl: string;
  public headerHeight = 54;
  public drawingHeight = 0;
  public gridHeight = 0;
  public gridWidth = 0;
  public isDesktop = false;
  public isDesktopBoundary = 767;
  target: any;

  mousedown$: any;
  mousedownSubscription: any;
  clicked = false;
  doubleClicked = false;
  movingDivider = false;
  showFullpageModal = false;

  @ViewChild('grid') public grid: GridComponent;
  @ViewChild('pdfViewer') public pdfViewer: PdfJsViewerComponent;
  @ViewChild('divider', { static: false }) public divider: ElementRef<any>;
  @ViewChild('docNum') public docNumTemplate: any;

  constructor(
    private sanitizer: DomSanitizer,
    private httpClient: HttpClient, // for demo purpose, should not be here
    private route: ActivatedRoute,
    private router: Router,
    private store: Store<State>,
    private clipboardService: ClipboardService
  ) {
    this.application$ = this.store.select(getApplicationState);
  }

  setColumns(): void {

    this.columns = this.tableColumnsMeta().map((meta: ColumnModel) => {
      return {
        field: meta.field,
        headerText: meta.headerText,
        width: 'auto',
        clipMode: 'EllipsisWithTooltip',
        textAlign: 'Left',
        minWidth: meta.minWidth,
        maxWidth: meta.maxWidth,
        visible: meta.visible,
        template: meta.template
      };
    });

  }

  tableColumnsMeta(): ColumnModel[] {

    // ['date', 'number', 'type', 'includedInfrastructure', 'sensitive', 'url']
    return [
      {
        field: 'drawNumber',
        headerText: 'Plan Number',
        minWidth: 100,
        maxWidth: 200,
        visible: true,
      },
      {
        field: 'fileName',
        headerText: 'File Name',
        minWidth: 100,
        maxWidth: 200,
        visible: true,
        template: this.docNumTemplate,
      }
    ];
  }

  ngOnInit(): void {
    this.application$.pipe(
      takeUntil(this.ngUnsubscribe)
    ).subscribe((state: ApplicationState) => {
      this.applicationState = state;
    });

    if (this.route.snapshot && this.route.snapshot.params) {

      const listOfDrawingsKey = 'numbers';
      const aocCoordKey = 'areaOfInterest';
      const projName = 'projectName';
      const projType = 'type';
      const listOfDrawings: string = this.route.snapshot.params[listOfDrawingsKey]; // https://localhost:44308/engdrawings;numbers=dwg1,dwg2
      const aocCoord: string = this.route.snapshot.params[aocCoordKey]; // https://localhost:44308/engdrawings;areaOfInterest=%5B0,1,2,3%5D

      if (listOfDrawings) {

        // TODO move this to an effect
        this.httpClient.get('api/postingplans?numbers=' + listOfDrawings).toPromise()
          .then((drawings: PostingPlanDetail[]) => {
            this.setColumns();
            this.drawings = drawings;
            if (drawings.length === 1) {
              this.selectedPlanUrl = this.drawings[0].url; // this.sanitizer.bypassSecurityTrustResourceUrl(this.drawings[0].url);
              if (this.pdfViewer) {
                this.pdfViewer.pdfSrc = this.selectedPlanUrl;
                this.pdfViewer.refresh();
              }

            }
          });

      } else if (aocCoord) {

        // TODO move this to an effect
        this.httpClient.get('api/postingplans?areaOfInterest=' + aocCoord).toPromise()
          .then((drawings: PostingPlanDetail[]) => {
            this.setColumns();
            this.drawings = drawings;
          });
      }
      else {
        this.store.dispatch(
          new ToastUserNotification({
            type: 'fail',
            title: 'Posting Plans',
            message: 'You need to provide either: drawing numbers or an areaOfInterest.',
            options: {
              disableTimeOut: true,
              closeButton: true
            }
          })
        );

      }

    }

    if (this.drawingHeight === 0) {
      this.setHeaderHeight();
    }

    this.adjustGridWidth();
  }

  ngAfterViewInit(): void {

    if (this.applicationState.windowInnerWidth > this.isDesktopBoundary) {
      this.isDesktop = true;
    }

    // Listen for a mouse down event on the divider to start moving it
    this.mousedown$ = fromEvent(this.divider.nativeElement, 'mousedown');
    this.mousedownSubscription = this.mousedown$
      .subscribe((e: any) => {
        this.movingDivider = true;

        setTimeout(() => {
          if (this.doubleClicked || this.clicked) {
            this.clicked = false;
            this.movingDivider = false;
            this.showFullpageModal = false;
          } else {
            this.showFullpageModal = true;
          }
        }, 650);

      });
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  @HostListener('window:mousemove', ['$event'])
  onMouseMove(event: any): void {
    if (this.movingDivider) {
      this.gridWidth = Math.max(0, event.x - 10);
      this.unselectText();
    }
  }

  isPlanSelected(): boolean {
    if (this.grid) {
      return this.nbOfPlanSelected() > 0;
    }
    return false;
  }

  nbOfPlanSelected(): number {
    if (this.grid) {
      return this.grid.getSelectedRowIndexes().length;
    }
    return 0;
  }

  singlePlanSelected(): boolean {
    if (this.grid) {
      return (null !== this.selectedPlanUrl || undefined  !== this.selectedPlanUrl);
    }
    return false;
  }

  clearFullpageModal() {
    this.movingDivider = false;
    this.showFullpageModal = false;
  }

  handlerClick() {
    this.clicked = true;
    this.movingDivider = false;
    this.showFullpageModal = false;
  }

  doubleClick() {
    this.doubleClicked = true;

    if (this.gridWidth > 0) {
      this.gridWidth = 0;
    } else {
      this.adjustGridWidth();

      this.unselectText();
    }

    setTimeout(() => {
      this.doubleClicked = false;
      this.movingDivider = false;
      this.showFullpageModal = false;
    }, 1000);
  }

  unselectText(): void {
    if (window.getSelection) {
      window.getSelection().removeAllRanges();
    }
  }

  setHeaderHeight(): void {
    this.drawingHeight = this.applicationState.windowInnerHeight - this.headerHeight;
    this.gridHeight = this.drawingHeight - 50;
  }

  adjustGridWidth(onResize: boolean = false): void {
    // Leave 10px for the divider
    if (this.applicationState.windowInnerWidth < this.isDesktopBoundary && !(0 === this.gridWidth && onResize)) {
      this.gridWidth = this.applicationState.windowInnerWidth;
    } else {
      if (0 === this.gridWidth && !onResize) {
        this.gridWidth = this.applicationState.windowInnerWidth / 2 - 10;
      }
    }
  }

  toggleSelecting(args: RowSelectEventArgs | RowDeselectEventArgs): void {
    if (args.target.classList.contains('no-select')) {
      (args as any).cancel = true;
      const data: any = args.data;
      if (data && data.url) {
        if (this.isDesktop) {
          this.selectedPlanUrl = data.url; // this.sanitizer.bypassSecurityTrustResourceUrl(data.url);
          if (this.pdfViewer) {
            this.pdfViewer.pdfSrc = this.selectedPlanUrl;
            this.pdfViewer.refresh();
          }
        } else {
          const url = this.sanitizer.sanitize(SecurityContext.RESOURCE_URL,
            this.sanitizer.bypassSecurityTrustResourceUrl(data.url)
          );
          this.store.dispatch(new OpenEngDrawing(url));
        }
      }

    }
  }

  rowSelected(args: RowSelectEventArgs | RowDeselectEventArgs): void {
    if (args.target.classList.contains('no-select')) {
      (args as any).cancel = true;
    }
  }

  rowDeSelected(args: RowDeselectEventArgs): void {
    if (args.target.classList.contains('no-select')) {
      (args as any).cancel = true;
    }

  }

  drawingFrameMessage(): string {
    return 'Click on drawing number to view.';
  }

  downloadFile() {
    const urls = this.grid.getSelectedRowIndexes().sort().map(index => (this.grid.currentViewData[index] as any).url);

    let timestamp = '';
    if (urls.length > 1) {
      const today = new Date(Date.now());
      const monPart = this.formatTimePart(today.getMonth() + 1);
      const datePart = this.formatTimePart(today.getDate());
      const hrPart = this.formatTimePart(today.getHours());
      const minPart = this.formatTimePart(today.getMinutes());
      const secsPart = this.formatTimePart(today.getSeconds());
      timestamp = today.getFullYear() + monPart.toString() + datePart.toString() + '-' + hrPart.toString() + minPart.toString() + secsPart.toString();
    }

    const fileName = 'eng-drawings-' + timestamp + '.zip';
    this.saveToZip(fileName, urls);
  }

  formatTimePart(datePart: number) {
    if (datePart < 10) {
      return '0' + datePart;
    }

    return datePart;
  }

  saveToZip(filename: any, urls: any) {
    if (urls.length > 1) {
      const zip = new JSZip();

      urls.forEach((url: any) => {
        const blobPromise = fetch(url).then(r => {
          if (r.status === 200) {
            return r.blob();
          }
          return Promise.reject(new Error(r.statusText));
        });
        const name = url.substring(url.lastIndexOf('/') + 1);
        zip.file(name, blobPromise);
      });

      zip.generateAsync({ type: 'blob' })
        .then((blob: any) => saveAs(blob, filename))
        .catch((e: any) => console.log(e));
    } else {
      const name = urls[0].substring(urls[0].lastIndexOf('/') + 1);
      saveAs(urls[0], name);
    }

  }

  copyUrlsToClipboard(): void {
    if (this.grid) {
      const urls = this.grid.getSelectedRowIndexes().sort().map(index => (this.grid.currentViewData[index] as PostingPlanDetail).url).join('\r\n');
      this.clipboardService.copyFromContent(urls);

      this.store.dispatch(new ToastUserNotification({
        type: 'success',
        title: 'Drawing(s) URL Copied to Clipboard',
        message: '',
        options: {}
      }));

    }
  }

  public gridCreated(): void {
    setSpinner({ type: 'Bootstrap' });
    this.grid.showSpinner();
  }

}
